/* Attention: problems across the person's Accounts that need someone to act, most
   urgent first, each with one action and ×. Three or more Accounts with the same
   problem fold into one group row. × snoozes for 24 hours, for this person only;
   if the problem is still there afterwards, it comes back. */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { Account } from '../../api/client';
import { C } from '../../components/ChatAssistant/compact/tokens';
import { font as f } from '../../components/ChatAssistant/compact/style';
import { Icon } from '../../components/ChatAssistant/compact/kit';
import { ConfirmDialog, type ConfirmSpec } from '../../components/Payments/ui';
import { toastQueue } from '../../components/ToastProvider';
import { pick } from '../../components/Statistics/unified/model';
import { accountName, attentionItems, attentionRows, isSnoozed, type AttentionItem, type AttentionRow } from '../attention';
import { periodRange, useDash, useSource } from '../context';
import type { AttentionKind, WidgetOf } from '../model';
import { BodyNote, WidgetFrame, type FrameProps } from '../WidgetFrame';

const KIND_ICON: Record<AttentionKind, string> = {
  killed: 'octagon',
  signed_out: 'log-out',
  billing_suspended: 'credit-card',
  plan_expiring: 'calendar',
  ai_paused: 'pause-circle',
  throttled: 'alert-triangle',
};
const ROW_H = 56;
const SNOOZED_BTN_H = 40;
/** Room for the "All clear" note. */
const EMPTY_H = 110;

export function Attention(p: FrameProps & { widget: WidgetOf<'needs'> }) {
  const { t } = useTranslation('dashboardWidgets');
  const navigate = useNavigate();
  const { accounts, subs, throttled, snoozes, setSnoozed, access, money, today, hasStatsOn, canToggle, openAccount, reconnect, setEnabled } = useDash();
  const c = p.widget.cfg;
  // Within a kind, highest revenue today first — when the person may see it.
  const stats = useSource('stats', access.stats ? periodRange(0, today) : null);
  const [open, setOpen] = useState<Set<AttentionKind>>(new Set());
  const [showSnoozed, setShowSnoozed] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmSpec | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());

  const now = Date.now();
  const all = useMemo(
    () => attentionItems(accounts, subs, throttled).filter((i) => (i.kind === 'killed' || c.inc.includes(i.kind)) && (!c.urgent || i.urgent)),
    [accounts, subs, throttled, c.inc, c.urgent],
  );
  const active = all.filter((i) => !isSnoozed(snoozes, i.key, now));
  const snoozed = all.filter((i) => isSnoozed(snoozes, i.key, now));
  const revenueOf = useMemo(() => {
    if (!stats.data) return undefined;
    const byId = new Map(stats.data.money.accounts.map((a) => [a.account_id, pick(a.revenue, money)]));
    return (id: string) => (hasStatsOn(id) ? byId.get(id) ?? 0 : 0);
  }, [stats.data, money, hasStatsOn]);
  const rows = attentionRows(active, revenueOf);
  const badge = new Set(active.map((i) => i.account.account_id)).size;
  const fit = !rows.length && !showSnoozed
    ? EMPTY_H
    : ROW_H * rows.reduce((n, r) => n + 1 + (r.type === 'group' && open.has(r.kind) ? r.items.length : 0), 0)
      + (snoozed.length ? SNOOZED_BTN_H + (showSnoozed ? ROW_H * snoozed.length : 0) : 0);

  const resume = async (targets: Account[]) => {
    const ids = targets.map((a) => a.account_id);
    setBusy((b) => new Set([...b, ...ids]));
    const r = await setEnabled(targets, true);
    setBusy((b) => new Set([...b].filter((id) => !ids.includes(id))));
    toastQueue.add({ title: r.failed ? t('needs.resumePartial', { ok: r.ok, failed: r.failed }) : t('needs.resumed', { count: r.ok }) });
  };

  const action = (i: AttentionItem): { label: string; run: () => void } => {
    const a = i.account;
    switch (i.kind) {
      case 'killed': return { label: t('needs.act.killed'), run: () => navigate(`/account/${a.account_id}/settings`) };
      case 'signed_out': return { label: t('needs.act.signed_out'), run: () => reconnect(a) };
      case 'billing_suspended': return { label: t('needs.act.billing_suspended'), run: () => navigate('/payments?topup=1') };
      case 'plan_expiring': return { label: t('needs.act.plan_expiring'), run: () => navigate('/payments') };
      case 'ai_paused': return canToggle(a.account_id)
        ? { label: t('needs.act.ai_paused'), run: () => void resume([a]) }
        : { label: t('needs.act.throttled'), run: () => openAccount(a) };
      case 'throttled': return { label: t('needs.act.throttled'), run: () => openAccount(a) };
    }
  };

  const text = (i: AttentionItem) => {
    const name = accountName(i.account);
    if (i.kind === 'plan_expiring') {
      return {
        title: i.daysLeft ? t('needs.title.plan_expiring', { name, count: i.daysLeft }) : t('needs.title.plan_expiring_today', { name }),
        sub: t('needs.sub.plan_expiring'),
      };
    }
    return { title: t(`needs.title.${i.kind}`, { name }), sub: t(`needs.sub.${i.kind}`) };
  };

  const iconBox = (kind: AttentionKind, urgent: boolean) => (
    <span style={{ width: 32, height: 32, flex: 'none', borderRadius: 8, background: urgent ? C.tint20 : C.surface, color: urgent ? C.purplePale : C.sub, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon name={KIND_ICON[kind]} size={15} />
    </span>
  );
  const lines = (title: string, sub: string) => (
    <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ font: f(600, 13), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</span>
      <span style={{ font: f(400, 12), color: C.sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</span>
    </span>
  );
  const dismiss = (keys: string[], back = false) => (
    <button
      type="button"
      className="dbw-icon-btn"
      onClick={() => setSnoozed(keys, !back)}
      title={back ? t('needs.unsnooze') : t('needs.snooze')}
      aria-label={back ? t('needs.unsnooze') : t('needs.snooze')}
      style={{ color: C.muted }}
    >
      <Icon name={back ? 'rotate-ccw' : 'x'} size={14} />
    </button>
  );
  const rowStyle = (indent = false): React.CSSProperties => ({
    height: ROW_H, flex: 'none', padding: indent ? '0 14px 0 36px' : '0 14px', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: 12, borderBottom: `1px solid ${C.border}`,
  });

  const itemRow = (i: AttentionItem, indent = false, back = false) => {
    const { title, sub } = text(i);
    const act = action(i);
    return (
      <div key={i.key} style={{ ...rowStyle(indent), opacity: back ? 0.6 : 1 }}>
        {iconBox(i.kind, i.urgent)}
        {lines(title, sub)}
        {!back && (
          <button type="button" className={`dbw-act ${i.urgent ? 'is-urgent' : ''}`} disabled={busy.has(i.account.account_id)} onClick={act.run}>{act.label}</button>
        )}
        {dismiss([i.key], back)}
      </div>
    );
  };

  const groupRow = (r: Extract<AttentionRow, { type: 'group' }>) => {
    const expanded = open.has(r.kind);
    const names = r.items.slice(0, 3).map((i) => accountName(i.account)).join(', ');
    const more = r.items.length - 3;
    const resumable = r.kind === 'ai_paused' ? r.items.map((i) => i.account).filter((a) => canToggle(a.account_id)) : [];
    const toggle = () => setOpen((o) => { const n = new Set(o); if (n.has(r.kind)) n.delete(r.kind); else n.add(r.kind); return n; });
    return (
      <div key={`g:${r.kind}`} style={{ display: 'flex', flexDirection: 'column' }}>
        <div style={{ ...rowStyle(), cursor: 'pointer' }} onClick={toggle}>
          {iconBox(r.kind, r.urgent)}
          {lines(t(`needs.group.${r.kind}`, { count: r.items.length }), more > 0 ? t('needs.groupNames', { names, count: more }) : names)}
          {resumable.length > 0 && (
            <button
              type="button"
              className="dbw-act"
              disabled={resumable.some((a) => busy.has(a.account_id))}
              onClick={(e) => {
                e.stopPropagation();
                setConfirm({
                  title: t('needs.confirmResumeTitle', { count: resumable.length }),
                  body: t('needs.confirmResumeBody', { count: resumable.length }),
                  confirm: t('needs.resumeAll'),
                  icon: 'play',
                  run: () => resume(resumable),
                });
              }}
            >
              {t('needs.resumeAll')}
            </button>
          )}
          <button
            type="button"
            className="dbw-icon-btn"
            aria-expanded={expanded}
            title={expanded ? t('needs.collapse') : t('needs.expand')}
            aria-label={expanded ? t('needs.collapse') : t('needs.expand')}
            onClick={(e) => { e.stopPropagation(); toggle(); }}
            style={{ color: C.sub }}
          >
            <Icon name={expanded ? 'chevron-up' : 'chevron-down'} size={15} />
          </button>
          <span onClick={(e) => e.stopPropagation()} style={{ display: 'flex' }}>{dismiss(r.items.map((i) => i.key))}</span>
        </div>
        {expanded && r.items.map((i) => itemRow(i, true))}
      </div>
    );
  };

  return (
    <WidgetFrame {...p} badge={badge} meta={t('needs.meta')} fit={fit}>
      {!rows.length && !showSnoozed ? (
        <BodyNote icon="check-circle" action={snoozed.length ? { label: t('needs.showSnoozed', { count: snoozed.length }), onClick: () => setShowSnoozed(true) } : undefined}>
          {t('needs.allClear')}
        </BodyNote>
      ) : (
        <div className="dbw-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
          {rows.map((r) => (r.type === 'group' ? groupRow(r) : itemRow(r.item)))}
          {snoozed.length > 0 && (
            <button type="button" className="dbw-link-btn" onClick={() => setShowSnoozed((s) => !s)} style={{ height: SNOOZED_BTN_H, flex: 'none', padding: '0 14px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name={showSnoozed ? 'chevron-up' : 'chevron-down'} size={13} />
              {showSnoozed ? t('needs.hideSnoozed') : t('needs.showSnoozed', { count: snoozed.length })}
            </button>
          )}
          {showSnoozed && snoozed.map((i) => itemRow(i, false, true))}
        </div>
      )}
      {confirm && <ConfirmDialog spec={confirm} onClose={() => setConfirm(null)} />}
    </WidgetFrame>
  );
}
